import { useMemo, useState } from 'react';

import { Route42InlineMarkdown, Route42Markdown } from '../../../components/ui/Route42Markdown';
import { HumanReconciliationReview } from './HumanReconciliationReview';
import type {
  ActiveReasoningInquiryDto,
  ActiveReasoningStateDto,
  InquiryResponseActionDto,
  ReasoningEvidenceReferenceDto,
  ReasoningEvidenceSnapshotDto,
  ReasoningFreshnessDto,
  ReasoningHistoryEventDto,
} from '../types/active-reasoning';

interface ActiveReasoningPanelProps {
  reasoning: ActiveReasoningStateDto | null;
  inquiries: ActiveReasoningInquiryDto[];
  freshness: ReasoningFreshnessDto | null;
  history: ReasoningHistoryEventDto[];
  canContribute: boolean;
  canAddEvidence: boolean;
  canReviewRelationships?: boolean;
  investigationId?: string;
  busyInquiryId?: string | null;
  actionError?: string | null;
  retryBusy?: boolean;
  onRetryReasoning?: () => Promise<void>;
  onRespond: (inquiry: ActiveReasoningInquiryDto, responseType: InquiryResponseActionDto, text?: string) => Promise<void>;
  onAddEvidence: (inquiry: ActiveReasoningInquiryDto) => void;
  onOpenEvidenceReference?: (reference: string) => void;
}

type ResponseMode = 'ANSWERED' | 'NOT_KNOWABLE' | 'NOT_RELEVANT' | 'CORRECT_PREMISE' | null;

const activeStatuses = new Set(['CANDIDATE', 'RECOMMENDED', 'PRESENTED']);
const inactiveStatuses = new Set(['ANSWERED', 'EVIDENCE_ADDED', 'NOT_KNOWABLE', 'NOT_RELEVANT', 'SUPERSEDED', 'RESOLVED']);

export function ActiveReasoningPanel({
  reasoning,
  inquiries,
  freshness,
  history,
  canContribute,
  canAddEvidence,
  canReviewRelationships = false,
  investigationId,
  busyInquiryId,
  actionError,
  retryBusy = false,
  onRetryReasoning,
  onRespond,
  onAddEvidence,
  onOpenEvidenceReference,
}: ActiveReasoningPanelProps) {
  const [responseMode, setResponseMode] = useState<ResponseMode>(null);
  const [responseText, setResponseText] = useState('');
  const recommendation = reasoning?.inquiry_recommendation_state ?? null;
  const recommendedId = recommendation?.inquiry_id ?? reasoning?.recommended_inquiry ?? null;
  const recommended = inquiries.find((item) => item.inquiry_id === recommendedId) ?? null;
  const otherOpen = useMemo(
    () => inquiries.filter((item) => item.inquiry_id !== recommendedId && activeStatuses.has(item.status)),
    [inquiries, recommendedId],
  );
  const past = useMemo(() => inquiries.filter((item) => inactiveStatuses.has(item.status)), [inquiries]);
  const activeHypotheses = reasoning?.hypotheses.filter((item) => !['CONTRADICTED', 'RESOLVED', 'SUPERSEDED'].includes(item.status)) ?? [];
  const openUncertainties = reasoning?.uncertainties.filter((item) => item.status.toUpperCase() !== 'RESOLVED') ?? [];

  async function submitResponse(responseType: InquiryResponseActionDto) {
    if (!recommended) return;
    await onRespond(recommended, responseType, responseText.trim() || undefined);
    setResponseText('');
    setResponseMode(null);
  }

  const stopState = recommendation?.status === 'NO_FURTHER_INQUIRY_WARRANTED';

  return (
    <section className="r42-investigation-section r42-active-reasoning" aria-labelledby="active-reasoning-title">
      <div className="r42-investigation-section-head r42-active-reasoning__head">
        <div>
          <span className="r42-eyebrow">Active Reasoning</span>
          <h2 id="active-reasoning-title">What Route42 recommends investigating next</h2>
        </div>
        {canContribute && reasoning && (freshness?.reasoning_status === 'CURRENT' || freshness?.reasoning_status === 'STALE') && onRetryReasoning ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary r42-active-reasoning__refresh"
            disabled={retryBusy}
            onClick={() => void onRetryReasoning()}
            title="Reevaluate Active Reasoning using the Investigation's current evidence"
          >
            <i className={`bi ${retryBusy ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'}`} aria-hidden="true" />
            {retryBusy ? 'Refreshing…' : 'Refresh reasoning'}
          </button>
        ) : null}
      </div>

      <ReasoningFreshnessNotice freshness={freshness} canRetry={canContribute} retryBusy={retryBusy} onRetry={onRetryReasoning} />

      {!reasoning ? (
        <div className="r42-active-reasoning__empty">
          <i className="bi bi-compass" aria-hidden="true" />
          <div>
            <strong>Route42 needs more Investigation evidence before it can recommend what to investigate next.</strong>
            <span>Add grounded evidence or continue the shared Investigation. Route42 will surface a next inquiry when the evidence supports one.</span>
          </div>
        </div>
      ) : stopState ? (
        <NoFurtherInquiry recommendation={recommendation} />
      ) : recommended ? (
        <article className={`r42-recommended-inquiry${isReasoningProvisional(freshness) ? ' r42-recommended-inquiry--provisional' : ''}`}>
          <div className="r42-recommended-inquiry__label">
            <i className="bi bi-compass-fill" aria-hidden="true" />
            <span>Route42 recommends investigating next</span>
          </div>
          <h3>{recommended.question_or_request}</h3>
          <div className="r42-recommended-inquiry__why">
            <strong>Why this matters</strong>
            <Route42Markdown content={recommendation?.rationale || recommended.rationale} />
          </div>
          <InquiryContext inquiry={recommended} reasoning={reasoning} />

          {canContribute ? (
            <div className="r42-recommended-inquiry__actions" aria-label="Recommended inquiry actions">
              {supportsDirectAnswer(recommended) ? <button type="button" className="btn r42-btn-primary" disabled={busyInquiryId === recommended.inquiry_id} onClick={() => { setResponseMode('ANSWERED'); setResponseText(''); }}><i className="bi bi-chat-left-text" aria-hidden="true" /> Answer</button> : null}
              {canAddEvidence ? <button type="button" className="btn btn-outline-secondary" disabled={busyInquiryId === recommended.inquiry_id} onClick={() => onAddEvidence(recommended)}><i className="bi bi-paperclip" aria-hidden="true" /> Add evidence</button> : null}
              <button type="button" className="r42-active-reasoning__text-action" disabled={busyInquiryId === recommended.inquiry_id} onClick={() => { setResponseMode('NOT_KNOWABLE'); setResponseText(''); }}><i className="bi bi-question-circle" aria-hidden="true" /> Not knowable</button>
              <button type="button" className="r42-active-reasoning__text-action" disabled={busyInquiryId === recommended.inquiry_id} onClick={() => { setResponseMode('NOT_RELEVANT'); setResponseText(''); }}><i className="bi bi-slash-circle" aria-hidden="true" /> Not relevant</button>
              <button type="button" className="r42-active-reasoning__text-action" disabled={busyInquiryId === recommended.inquiry_id} onClick={() => { setResponseMode('CORRECT_PREMISE'); setResponseText(''); }}><i className="bi bi-pencil-square" aria-hidden="true" /> Correct detail</button>
            </div>
          ) : null}

          {responseMode ? (
            <form className="r42-inquiry-response" onSubmit={(event) => { event.preventDefault(); void submitResponse(responseMode); }}>
              {responseMode === 'NOT_KNOWABLE' ? (
                <>
                  <strong>Mark this information as unavailable?</strong>
                  <p>Route42 will preserve the unresolved uncertainty and consider other evidence paths rather than asking for this unavailable information again.</p>
                </>
              ) : (
                <>
                  <label htmlFor="r42-inquiry-response-text">{responseMode === 'ANSWERED' ? 'Your answer' : responseMode === 'NOT_RELEVANT' ? 'Why does this not apply?' : 'What should Route42 correct?'}</label>
                  <textarea
                    id="r42-inquiry-response-text"
                    rows={3}
                    maxLength={4000}
                    value={responseText}
                    onChange={(event) => setResponseText(event.target.value)}
                    placeholder={responseMode === 'ANSWERED' ? 'Share what you directly know. Route42 will treat this as human-provided evidence, not automatic fact.' : 'Add the context Route42 should use when reevaluating this inquiry.'}
                    autoFocus
                  />
                </>
              )}
              <div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setResponseMode(null); setResponseText(''); }}>Cancel</button>
                <button type="submit" className="btn btn-sm r42-btn-primary" disabled={(responseMode !== 'NOT_KNOWABLE' && !responseText.trim()) || busyInquiryId === recommended.inquiry_id}>{busyInquiryId === recommended.inquiry_id ? 'Recording…' : responseMode === 'NOT_KNOWABLE' ? 'Mark unavailable' : 'Submit'}</button>
              </div>
            </form>
          ) : null}
          {actionError ? <div className="r42-question-error" role="alert"><i className="bi bi-exclamation-circle" /><span>{actionError}</span></div> : null}
        </article>
      ) : (
        <div className="r42-active-reasoning__empty">
          <i className="bi bi-check2-circle" aria-hidden="true" />
          <div><strong>No next inquiry is available from the current reasoning state.</strong><span>Route42 is preserving the Investigation's unresolved limitations rather than manufacturing another question.</span></div>
        </div>
      )}

      {reasoning && investigationId ? <HumanReconciliationReview
        investigationId={investigationId}
        reasoningVersion={reasoning.reasoning_version}
        canReview={canReviewRelationships}
        evidenceSnapshot={reasoning.evidence_snapshot}
        onOpenEvidenceReference={onOpenEvidenceReference}
      /> : null}

      {reasoning ? (
        <div className="r42-active-reasoning__details">
          <details open={otherOpen.length > 0}>
            <summary><span>Other unresolved questions</span><strong>{otherOpen.length}</strong></summary>
            <div className="r42-inquiry-queue">
              {otherOpen.length ? otherOpen.map((item) => <InquiryQueueItem key={item.inquiry_id} inquiry={item} />) : <p>No other material inquiry is currently active.</p>}
            </div>
          </details>

          <details>
            <summary><span>Current hypotheses</span><strong>{activeHypotheses.length}</strong></summary>
            <div className="r42-hypothesis-list">
              {activeHypotheses.length ? activeHypotheses.map((hypothesis) => (
                <details className="r42-hypothesis-card" key={hypothesis.hypothesis_id}>
                  <summary><span>{hypothesis.statement}</span><StatusPill value={hypothesis.status} /></summary>
                  <div className="r42-hypothesis-card__body">
                    {hypothesis.origin === 'HUMAN_PROPOSED' ? <p className="r42-active-reasoning__provenance"><i className="bi bi-person" aria-hidden="true" /> {hypothesis.origin_attribution || 'Team-proposed hypothesis'}</p> : null}
                    <EvidenceGroup title="Evidence supporting" refs={hypothesis.supporting_evidence} snapshot={reasoning.evidence_snapshot} onOpen={onOpenEvidenceReference} />
                    <EvidenceGroup title="Evidence against" refs={hypothesis.contradicting_evidence} snapshot={reasoning.evidence_snapshot} onOpen={onOpenEvidenceReference} />
                    <EvidenceGroup title="Relevant but inconclusive" refs={hypothesis.inconclusive_evidence} snapshot={reasoning.evidence_snapshot} onOpen={onOpenEvidenceReference} />
                    {hypothesis.related_uncertainties.length ? <div className="r42-active-reasoning__unknown"><strong>Still unresolved</strong>{hypothesis.related_uncertainties.map((id) => <span key={id}>{uncertaintyText(reasoning, id)}</span>)}</div> : null}
                  </div>
                </details>
              )) : <p>No sufficiently supported hypothesis is currently active.</p>}
            </div>
          </details>

          <details>
            <summary><span>Uncertainty & conflicting evidence</span><strong>{openUncertainties.length}</strong></summary>
            <div className="r42-uncertainty-list">
              {openUncertainties.length ? openUncertainties.map((uncertainty) => (
                <article className="r42-uncertainty-card" key={uncertainty.uncertainty_id}>
                  <div><StatusPill value={uncertaintyLabel(uncertainty.type)} /><strong>{uncertainty.statement}</strong></div>
                  <p>{uncertainty.why_it_matters}</p>
                  <EvidenceGroup title="Supporting evidence" refs={uncertainty.supporting_evidence} snapshot={reasoning.evidence_snapshot} onOpen={onOpenEvidenceReference} />
                  <EvidenceGroup title="Conflicting evidence" refs={uncertainty.contradicting_evidence} snapshot={reasoning.evidence_snapshot} onOpen={onOpenEvidenceReference} />
                  {uncertainty.missing_evidence.length ? (
                    <MissingEvidenceGroup
                      items={uncertainty.missing_evidence}
                      uncertaintyId={uncertainty.uncertainty_id}
                      uncertaintyStatement={uncertainty.statement}
                      inquiries={inquiries}
                      recommendedInquiryId={recommendedId}
                    />
                  ) : null}
                </article>
              )) : <p>No material uncertainty is currently open.</p>}
            </div>
          </details>

          <details>
            <summary><span>Reasoning history</span><strong>{history.length}</strong></summary>
            <div className="r42-reasoning-history">
              {history.length ? history.map((event) => (
                <article key={event.history_event_id}>
                  <span className="r42-reasoning-history__time">{formatTimestamp(event.occurred_at)}</span>
                  <div><strong>{event.title}</strong><p>{event.detail}</p></div>
                </article>
              )) : <p>Meaningful reasoning changes will appear here as the Investigation evolves.</p>}
            </div>
          </details>

          {past.length ? <details><summary><span>Resolved or unavailable questions</span><strong>{past.length}</strong></summary><div className="r42-inquiry-queue">{past.map((item) => <InquiryQueueItem key={item.inquiry_id} inquiry={item} />)}</div></details> : null}
        </div>
      ) : null}
    </section>
  );
}

function ReasoningFreshnessNotice({
  freshness,
  canRetry,
  retryBusy,
  onRetry,
}: {
  freshness: ReasoningFreshnessDto | null;
  canRetry: boolean;
  retryBusy: boolean;
  onRetry?: () => Promise<void>;
}) {
  if (!freshness || freshness.reasoning_status === 'CURRENT') return null;
  if (freshness.reasoning_status === 'NO_REASONING_YET') return <div className="r42-reasoning-freshness r42-reasoning-freshness--neutral"><i className="bi bi-compass" /><span>Route42 needs more Investigation evidence before it can recommend what to investigate next.</span></div>;
  if (freshness.reasoning_status === 'FAILED_REFRESH') return <div className="r42-reasoning-freshness r42-reasoning-freshness--warning"><i className="bi bi-exclamation-triangle" /><span>Route42 couldn't refresh the reasoning from the latest evidence. The previous analysis remains available.</span>{canRetry && onRetry ? <button type="button" className="btn btn-sm btn-outline-secondary" disabled={retryBusy} onClick={() => void onRetry()}>{retryBusy ? 'Retrying…' : 'Retry reasoning'}</button> : null}</div>;
  if (freshness.reasoning_status === 'STALE') return <div className="r42-reasoning-freshness r42-reasoning-freshness--warning"><i className="bi bi-clock-history" /><span>New evidence may affect this recommendation. Refresh reasoning to incorporate it.</span></div>;
  return <div className="r42-reasoning-freshness"><span className="spinner-border spinner-border-sm" aria-hidden="true" /><span>Route42 is updating this Investigation based on newly added evidence.</span></div>;
}

function NoFurtherInquiry({ recommendation }: { recommendation: NonNullable<ActiveReasoningStateDto['inquiry_recommendation_state']> }) {
  return <article className="r42-no-further-inquiry"><div className="r42-no-further-inquiry__icon"><i className="bi bi-check2-circle" aria-hidden="true" /></div><div><span className="r42-eyebrow">Route42 reasoning</span><h3>No additional inquiry is currently warranted.</h3><p>{recommendation.rationale}</p>{recommendation.unresolved_limitations.length ? <div className="r42-active-reasoning__limitations"><strong>Remaining limitations</strong>{recommendation.unresolved_limitations.map((item) => <span key={item}>{item}</span>)}</div> : null}</div></article>;
}

function InquiryContext({ inquiry, reasoning }: { inquiry: ActiveReasoningInquiryDto; reasoning: ActiveReasoningStateDto }) {
  const uncertainty = inquiry.target_uncertainties.map((id) => reasoning.uncertainties.find((item) => item.uncertainty_id === id)?.statement).filter(Boolean);
  const hypothesis = inquiry.target_hypotheses.map((id) => reasoning.hypotheses.find((item) => item.hypothesis_id === id)?.statement).filter(Boolean);
  if (!uncertainty.length && !hypothesis.length) return null;
  return <div className="r42-recommended-inquiry__context">{uncertainty.length ? <span><strong>Unresolved:</strong> {uncertainty.join(' ')}</span> : null}{hypothesis.length ? <span><strong>Could clarify:</strong> {hypothesis.join(' ')}</span> : null}</div>;
}

function InquiryQueueItem({ inquiry }: { inquiry: ActiveReasoningInquiryDto }) {
  return <article className="r42-inquiry-queue__item"><div><StatusPill value={inquiryStatusLabel(inquiry.status)} /><span className="r42-inquiry-queue__type">{inquiryTypeLabel(inquiry.inquiry_type)}</span></div><Route42InlineMarkdown content={inquiry.question_or_request} />{inquiry.rationale ? <small>{inquiry.rationale}</small> : null}</article>;
}

function MissingEvidenceGroup({
  items,
  uncertaintyId,
  uncertaintyStatement,
  inquiries,
  recommendedInquiryId,
}: {
  items: string[];
  uncertaintyId: string;
  uncertaintyStatement: string;
  inquiries: ActiveReasoningInquiryDto[];
  recommendedInquiryId: string | null;
}) {
  const readable = items
    .map((item) => item.trim())
    .filter((item) => item && !isOpaqueInternalReference(item) && !isGenericMissingEvidence(item));

  const targetedInquiries = inquiries
    .filter((inquiry) => activeStatuses.has(inquiry.status) && inquiry.target_uncertainties.includes(uncertaintyId))
    .sort((left, right) => {
      const leftRecommended = left.inquiry_id === recommendedInquiryId ? 0 : 1;
      const rightRecommended = right.inquiry_id === recommendedInquiryId ? 0 : 1;
      if (leftRecommended !== rightRecommended) return leftRecommended - rightRecommended;
      return (left.priority_rank ?? Number.MAX_SAFE_INTEGER) - (right.priority_rank ?? Number.MAX_SAFE_INTEGER);
    });

  const inquiryQuestion = targetedInquiries[0]?.question_or_request.trim();
  const fallback = inquiryQuestion
    ? `Evidence that directly addresses this question: ${inquiryQuestion}`
    : `Evidence that resolves this uncertainty: ${uncertaintyStatement.replace(/[.\s]+$/, '')}.`;
  const displayItems = readable.length ? Array.from(new Set(readable)) : [fallback];
  return <div className="r42-active-reasoning__unknown"><strong>Still needed</strong>{displayItems.map((item) => <span key={item}>{item}</span>)}</div>;
}

function isGenericMissingEvidence(value: string) {
  const normalized = value.trim().toLowerCase().replace(/\.$/, '');
  return new Set([
    'additional evidence is needed to resolve this uncertainty',
    'evidence that resolves this material uncertainty',
    'evidence that resolves the disputed proposition',
    'evidence or an attributable answer that resolves this question',
    'investigation-relevant evidence',
  ]).has(normalized);
}

function isOpaqueInternalReference(value: string) {
  return /(?:^|[^A-Za-z0-9_])(?:gap|kc|ic|ev|doc|fact|chunk|obs|msg|note|hyp|unc|inq)_[A-Za-z0-9][A-Za-z0-9_-]{7,}(?![A-Za-z0-9_-])/i.test(value);
}

function EvidenceGroup({
  title,
  refs,
  snapshot,
  onOpen,
}: {
  title: string;
  refs: string[];
  snapshot: ReasoningEvidenceSnapshotDto;
  onOpen?: (reference: string) => void;
}) {
  if (!refs.length) return null;
  return (
    <div className="r42-reasoning-evidence">
      <strong>{title}</strong>
      <div>
        {refs.map((ref, index) => {
          const item = findEvidenceReference(snapshot, ref);
          const label = evidenceDisplayLabel(item, index);
          return onOpen ? (
            <button type="button" key={ref} onClick={() => onOpen(ref)} title="Open this evidence">
              <i className="bi bi-paperclip" aria-hidden="true" />
              <span>{label}</span>
            </button>
          ) : (
            <span key={ref}>
              <i className="bi bi-paperclip" aria-hidden="true" />
              <span>{label}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function findEvidenceReference(snapshot: ReasoningEvidenceSnapshotDto, reference: string) {
  return [
    ...snapshot.investigation_evidence,
    ...snapshot.documents,
    ...snapshot.messages,
    ...snapshot.findings,
    ...snapshot.human_answers,
    ...snapshot.observations,
  ].find((item) => item.evidence_reference === reference);
}

function evidenceDisplayLabel(item: ReasoningEvidenceReferenceDto | undefined, index: number) {
  if (!item) return `Evidence item ${index + 1}`;
  const sourceLabel = evidenceSourceLabel(item.source_type);
  const location: Record<string, unknown> = item.source_location ?? {};
  const detail = [location['note'], location['text']].find((value): value is string => typeof value === 'string' && Boolean(value.trim()));
  if (detail) return `${sourceLabel} · ${truncateEvidenceDetail(detail)}`;
  if (item.document_section) return `${sourceLabel} · ${item.document_section}`;
  if (item.document_page != null) return `${sourceLabel} · page ${item.document_page}`;
  if (item.timestamp) return `${sourceLabel} · ${formatTimestamp(item.timestamp)}`;
  return `${sourceLabel} ${index + 1}`;
}

function evidenceSourceLabel(value: string) {
  return ({
    HUMAN_INQUIRY_RESPONSE: 'Team answer',
    USER_NOTE: 'Team update',
    EVIDENCE_BACKED_FINDING: 'Team finding',
    DOCUMENT: 'Document evidence',
    KNOWLEDGE_CHUNK: 'Machine knowledge',
    OBSERVATION: 'Observation',
  } as Record<string, string>)[value.toUpperCase()] ?? humanize(value);
}

function truncateEvidenceDetail(value: string) {
  const normalized = value.trim().replace(/\s+/g, ' ');
  if (normalized.length <= 72) return normalized;
  const candidate = normalized.slice(0, 73);
  const lastWhitespace = candidate.search(/\s+\S*$/);
  const cutoff = lastWhitespace > 48 ? lastWhitespace : 72;
  return `${normalized.slice(0, cutoff).trimEnd()}…`;
}

function StatusPill({ value }: { value: string }) { return <span className={`r42-reasoning-status r42-reasoning-status--${value.toLowerCase().replaceAll('_', '-').replaceAll(' ', '-')}`}>{humanize(value)}</span>; }
function supportsDirectAnswer(inquiry: ActiveReasoningInquiryDto) { return inquiry.inquiry_type === 'HUMAN_QUESTION' || inquiry.inquiry_type === 'CONFLICT_RESOLUTION'; }
function isReasoningProvisional(freshness: ReasoningFreshnessDto | null) { return freshness?.reasoning_status === 'REFRESH_PENDING' || freshness?.reasoning_status === 'PROCESSING' || freshness?.reasoning_status === 'STALE' || freshness?.reasoning_status === 'FAILED_REFRESH'; }
function uncertaintyText(reasoning: ActiveReasoningStateDto, id: string) { return reasoning.uncertainties.find((item) => item.uncertainty_id === id)?.statement ?? id; }
function uncertaintyLabel(value: string) {
  return ({
    CONFLICTING_EVIDENCE: 'Conflicting evidence',
    MISSING_INFORMATION: 'Unresolved',
    WEAK_SUPPORT: 'Limited support',
    CAUSAL_UNCERTAINTY: 'Causal link unresolved',
    TEMPORAL_UNCERTAINTY: 'Timing unclear',
    COMPETING_EXPLANATIONS: 'Multiple explanations',
    STALE_ASSUMPTION: 'May be outdated',
    SCOPE_UNCERTAINTY: 'Scope unclear',
  } as Record<string, string>)[value] ?? humanize(value);
}
function inquiryTypeLabel(value: string) { return humanize(value).replace('Human Question', 'Question').replace('Document Request', 'Evidence request'); }
function inquiryStatusLabel(value: string) {
  if (value === 'CANDIDATE' || value === 'PRESENTED') return 'Open';
  if (value === 'RECOMMENDED') return 'Recommended';
  if (value === 'NOT_KNOWABLE') return 'Unavailable';
  if (value === 'EVIDENCE_ADDED') return 'Evidence added';
  if (value === 'NOT_RELEVANT') return 'Not relevant';
  return humanize(value);
}
function humanize(value: string) { return value.toLocaleLowerCase().replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toLocaleUpperCase()); }
function formatTimestamp(value: string) { const date = new Date(value); return Number.isNaN(date.valueOf()) ? value : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date); }
